import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Folder,
  FolderOpen,
  FileText,
  Bot,
  Hammer,
  FileCode,
  Gavel,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Plus,
  Loader2,
  Copy,
  Search,
  X,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { cn } from '@/lib/utils';
import { contentApi } from '@/services/contentApi';
import { listBuiltinSkills, type BuiltinSkill } from '@/services/skillsApi';
import { NewItemDialog } from './NewItemDialog';
import { CloneDialog } from './CloneDialog';

interface ContentItem {
  id: string;
  name: string;
  content_type: string;
  folder_path?: string;
}

interface FolderItem {
  id: string;
  name: string;
  path: string;
}

interface FileTreeProps {
  onFileSelect: (item: ContentItem) => void;
  selectedItemId?: string;
}

const CONTENT_TYPES: Record<
  string,
  { label: string; icon: React.ElementType; color: string; folderPath: string }
> = {
  agent: { label: 'Agents', icon: Bot, color: 'text-blue-500', folderPath: '/agents' },
  skill: { label: 'Skills', icon: Hammer, color: 'text-purple-500', folderPath: '/skills' },
  prompt: { label: 'Prompts', icon: FileCode, color: 'text-orange-500', folderPath: '/prompts' },
  judge: { label: 'Judges', icon: Gavel, color: 'text-red-500', folderPath: '/judges' },
};

function FileTreeItem({
  item,
  depth = 0,
  selectedItemId,
  onSelect,
  onClone,
}: {
  item: ContentItem;
  depth?: number;
  selectedItemId?: string;
  onSelect: (item: ContentItem) => void;
  onClone?: (item: ContentItem) => void;
}) {
  const isSelected = item.id === selectedItemId;
  const typeConfig = CONTENT_TYPES[item.content_type] || {
    icon: FileText,
    color: 'text-muted-foreground',
  };
  const Icon = typeConfig.icon;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className={cn(
            'flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-muted/50 rounded-sm',
            isSelected && 'bg-muted'
          )}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onClick={() => onSelect(item)}
        >
          <Icon className={cn('h-4 w-4', typeConfig.color)} />
          <span className={cn('text-sm truncate', isSelected && 'font-medium')}>{item.name}</span>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => onClone?.(item)}>
          <Copy className="h-4 w-4 mr-2" />
          Clone
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function FolderTreeItem({
  folder,
  items,
  depth = 1,
  selectedItemId,
  onFileSelect,
  onClone,
  isExpanded,
  onToggle,
  isLoading,
}: {
  folder: FolderItem;
  items: ContentItem[];
  depth?: number;
  selectedItemId?: string;
  onFileSelect: (item: ContentItem) => void;
  onClone?: (item: ContentItem) => void;
  isExpanded: boolean;
  onToggle: () => void;
  isLoading: boolean;
}) {
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <div
          className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-muted/50 rounded-sm"
          style={{ paddingLeft: `${8 + depth * 16}px` }}
        >
          {isExpanded ? (
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Folder className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium flex-1">{folder.name}</span>
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : isExpanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {items.map((item) => (
          <FileTreeItem
            key={item.id}
            item={item}
            depth={depth + 1}
            selectedItemId={selectedItemId}
            onSelect={onFileSelect}
            onClone={onClone}
          />
        ))}
        {!isLoading && items.length === 0 && (
          <div
            className="text-xs text-muted-foreground py-1"
            style={{ paddingLeft: `${24 + depth * 16}px` }}
          >
            No items
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

// Builtin skills folder component
function BuiltinSkillsFolder({
  selectedItemId,
  onFileSelect,
}: {
  selectedItemId?: string;
  onFileSelect: (item: ContentItem) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [builtinSkills, setBuiltinSkills] = useState<BuiltinSkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleToggle = async () => {
    const wasExpanded = expanded;
    setExpanded(!wasExpanded);

    // Load builtin skills when first expanding
    if (!wasExpanded && !loaded) {
      setLoading(true);
      try {
        const skills = await listBuiltinSkills();
        setBuiltinSkills(skills);
        setLoaded(true);
      } catch (error) {
        console.error('Failed to load builtin skills:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Collapsible open={expanded} onOpenChange={handleToggle}>
      <CollapsibleTrigger asChild>
        <div
          className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-muted/50 rounded-sm"
          style={{ paddingLeft: '24px' }}
        >
          {expanded ? (
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Folder className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium flex-1">Builtins</span>
          <Lock className="h-3 w-3 text-muted-foreground" />
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : expanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {builtinSkills.map((skill) => (
          <div
            key={skill.name}
            className={cn(
              'flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-muted/50 rounded-sm',
              selectedItemId === `builtin:${skill.name}` && 'bg-muted'
            )}
            style={{ paddingLeft: '40px' }}
            onClick={() =>
              onFileSelect({
                id: `builtin:${skill.name}`,
                name: skill.name,
                content_type: 'skill',
              })
            }
          >
            <Hammer className="h-4 w-4 text-purple-500" />
            <span className="text-sm truncate">{skill.name}</span>
            <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
          </div>
        ))}
        {!loading && loaded && builtinSkills.length === 0 && (
          <div
            className="text-xs text-muted-foreground py-1"
            style={{ paddingLeft: '40px' }}
          >
            No builtin skills
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function ContentTypeSection({
  contentType,
  config,
  selectedItemId,
  onFileSelect,
  onClone,
}: {
  contentType: string;
  config: { label: string; icon: React.ElementType; color: string; folderPath: string };
  selectedItemId?: string;
  onFileSelect: (item: ContentItem) => void;
  onClone?: (item: ContentItem) => void;
}) {
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [rootItems, setRootItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [folderItems, setFolderItems] = useState<Record<string, ContentItem[]>>({});
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());

  const Icon = config.icon;

  useEffect(() => {
    loadContent();
  }, [contentType]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const rootFolderPath = config.folderPath;

      const [foldersData, itemsData] = await Promise.all([
        contentApi.listFolders(rootFolderPath, contentType),
        contentApi.listItems({ contentType, limit: 100 }),
      ]);

      setFolders(foldersData);

      const rootItemsList = itemsData.filter(
        (item: ContentItem) => !item.folder_path || item.folder_path === rootFolderPath
      );
      setRootItems(rootItemsList);

      const itemsByFolder: Record<string, ContentItem[]> = {};
      itemsData.forEach((item: ContentItem) => {
        if (item.folder_path && item.folder_path !== rootFolderPath) {
          if (!itemsByFolder[item.folder_path]) {
            itemsByFolder[item.folder_path] = [];
          }
          itemsByFolder[item.folder_path].push(item);
        }
      });
      setFolderItems(itemsByFolder);
    } catch (error) {
      console.error(`Failed to load ${contentType}s:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFolder = async (folderPath: string) => {
    const isExpanding = !expandedFolders.has(folderPath);

    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderPath)) {
        next.delete(folderPath);
      } else {
        next.add(folderPath);
      }
      return next;
    });

    if (isExpanding && !folderItems[folderPath]) {
      setLoadingFolders((prev) => new Set(prev).add(folderPath));
      try {
        const items = await contentApi.listItems({ folderPath, limit: 100 });
        setFolderItems((prev) => ({ ...prev, [folderPath]: items }));
      } catch (error) {
        console.error(`Failed to load items for folder ${folderPath}:`, error);
      } finally {
        setLoadingFolders((prev) => {
          const next = new Set(prev);
          next.delete(folderPath);
          return next;
        });
      }
    }
  };

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-muted/50 rounded-sm">
          <Icon className={cn('h-4 w-4', config.color)} />
          <span className="text-sm font-semibold flex-1">{config.label}</span>
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : expanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {/* Builtins folder for skills */}
        {contentType === 'skill' && (
          <BuiltinSkillsFolder
            selectedItemId={selectedItemId}
            onFileSelect={onFileSelect}
          />
        )}
        {folders.map((folder) => (
          <FolderTreeItem
            key={folder.id}
            folder={folder}
            items={folderItems[folder.path] || []}
            depth={1}
            selectedItemId={selectedItemId}
            onFileSelect={onFileSelect}
            onClone={onClone}
            isExpanded={expandedFolders.has(folder.path)}
            onToggle={() => handleToggleFolder(folder.path)}
            isLoading={loadingFolders.has(folder.path)}
          />
        ))}
        {rootItems.map((item) => (
          <FileTreeItem
            key={item.id}
            item={item}
            depth={1}
            selectedItemId={selectedItemId}
            onSelect={onFileSelect}
            onClone={onClone}
          />
        ))}
        {!loading && folders.length === 0 && rootItems.length === 0 && contentType !== 'skill' && (
          <div className="text-xs text-muted-foreground py-1 px-6">No {contentType}s</div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function FileTree({ onFileSelect, selectedItemId }: FileTreeProps) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [newItemDialogOpen, setNewItemDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [itemToClone, setItemToClone] = useState<ContentItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [allItems, setAllItems] = useState<ContentItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Load all items for search
  useEffect(() => {
    async function loadAllItems() {
      setIsSearching(true);
      try {
        const types = Object.keys(CONTENT_TYPES);
        const results = await Promise.all(
          types.map(type => contentApi.listItems({ contentType: type, limit: 500 }))
        );
        setAllItems(results.flat());
      } catch (error) {
        console.error('Failed to load items for search:', error);
      } finally {
        setIsSearching(false);
      }
    }
    loadAllItems();
  }, [refreshKey]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return allItems.filter(item =>
      item.name.toLowerCase().includes(query) ||
      item.content_type.toLowerCase().includes(query) ||
      (item.folder_path && item.folder_path.toLowerCase().includes(query))
    );
  }, [searchQuery, allItems]);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleItemCreated = useCallback((result: { type: string; item?: ContentItem }) => {
    handleRefresh();
    if (result.type === 'item' && result.item && onFileSelect) {
      onFileSelect(result.item);
    }
  }, [handleRefresh, onFileSelect]);

  const handleClone = useCallback((item: ContentItem) => {
    setItemToClone(item);
    setCloneDialogOpen(true);
  }, []);

  const handleCloned = useCallback((newItem: ContentItem) => {
    handleRefresh();
    if (onFileSelect) {
      onFileSelect(newItem);
    }
  }, [handleRefresh, onFileSelect]);

  return (
    <div className="h-full overflow-auto">
      {/* Header */}
      <div className="px-3 py-2 flex items-center justify-between border-b">
        <span className="text-sm font-semibold">Content</span>
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleRefresh}>
                <RefreshCw className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refresh</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setNewItemDialogOpen(true)}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>New Item</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Search */}
      <div className="px-2 py-2 border-b">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 pl-7 pr-7 text-xs"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Search Results or Content Type Sections */}
      {searchQuery.trim() ? (
        <div className="py-1">
          {isSearching ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <FileTreeItem
                key={item.id}
                item={item}
                depth={0}
                selectedItemId={selectedItemId}
                onSelect={onFileSelect}
                onClone={handleClone}
              />
            ))
          ) : (
            <div className="text-xs text-muted-foreground text-center py-4">
              No results for "{searchQuery}"
            </div>
          )}
        </div>
      ) : (
        <div key={refreshKey} className="py-1">
          {Object.entries(CONTENT_TYPES).map(([type, config]) => (
            <ContentTypeSection
              key={type}
              contentType={type}
              config={config}
              selectedItemId={selectedItemId}
              onFileSelect={onFileSelect}
              onClone={handleClone}
            />
          ))}
        </div>
      )}

      {/* New Item Dialog */}
      <NewItemDialog
        open={newItemDialogOpen}
        onClose={() => setNewItemDialogOpen(false)}
        onCreated={handleItemCreated}
      />

      {/* Clone Dialog */}
      <CloneDialog
        item={itemToClone}
        open={cloneDialogOpen}
        onOpenChange={setCloneDialogOpen}
        onCloned={handleCloned}
      />
    </div>
  );
}

export default FileTree;
